import apiClient, { getApiEndpoint } from '@/lib/apiClient';
import { handleApiError } from '@/lib/apiErrorUtils';
import {
  CancelQuoteDenied,
  CancelQuoteResult,
  CancelQuoteSuccess,
  CancelReason,
  GetCancelReasonsResponse,
  RawGetCancelReasonsApiResponse,
  RawQuoteApiResponse,
  RawQuoteEstimate,
} from './types';

/**
 * Retrieves all available cancellation reasons from the API.
 *
 * This function fetches the list of reasons that customers can select when
 * canceling their contracts. The reasons are transformed from the raw API
 * response format to a more user-friendly structure.
 *
 * @returns {Promise<GetCancelReasonsResponse>} A promise that resolves to an array of cancellation reasons
 * @throws {Error} Throws an error with a user-friendly message if the API request fails
 */
export const getCancelReasons = async (): Promise<GetCancelReasonsResponse> => {
  try {
    const endpoint = getApiEndpoint('/contracts/cancellation/reasons');
    const response =
      await apiClient.get<RawGetCancelReasonsApiResponse>(endpoint);
    const rawApiResponse = response.data;

    const transformedReasons: CancelReason[] = rawApiResponse.reasons.map(
      (rawReason) => ({
        id: rawReason.id,
        description: rawReason.name,
      })
    );

    return transformedReasons;
  } catch (err) {
    const userFriendlyMessage = handleApiError(
      err,
      'loading cancellation reasons'
    );
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Payload interface for submitting a cancellation quote request.
 *
 * @interface SubmitQuoteRequestPayload
 * @property {string} cancelDate - The desired cancellation date in ISO format (YYYY-MM-DD)
 * @property {number} cancelMileage - The vehicle's mileage at the time of cancellation
 * @property {number} cancelReasonId - The ID of the selected cancellation reason
 * @property {string[]} contractNumbers - Array of contract numbers to be canceled
 */
export interface SubmitQuoteRequestPayload {
  cancelDate: string;
  cancelMileage: number;
  cancelReasonId: number;
  contractNumbers: string[];
}

/**
 * Submits a cancellation quote request for one or more contracts.
 *
 * This function sends a request to generate cancellation quotes for the specified
 * contracts. It returns either successful quotes with refund amounts or denial
 * reasons for contracts that cannot be canceled.
 *
 * @param {SubmitQuoteRequestPayload} payload - The quote request data
 * @param {string} payload.cancelDate - The desired cancellation date in ISO format
 * @param {number} payload.cancelMileage - Vehicle mileage at cancellation time
 * @param {number} payload.cancelReasonId - ID of the cancellation reason
 * @param {string[]} payload.contractNumbers - Array of contract numbers to quote
 *
 * @returns {Promise<CancelQuoteResult[]>} A promise that resolves to an array of quote results,
 *   each containing either quote details (for approved cancellations) or denial reasons
 *
 * @throws {Error} Throws an error with a user-friendly message if the API request fails
 */
export const submitQuoteRequest = async (
  payload: SubmitQuoteRequestPayload
): Promise<CancelQuoteResult[]> => {
  try {
    const endpoint = getApiEndpoint('/contracts/cancellation/quotes');
    const response = await apiClient.get<RawQuoteApiResponse>(endpoint, {
      params: payload,
      paramsSerializer: {
        indexes: null,
      },
    });
    const rawApiResponse = response.data;

    const results: CancelQuoteResult[] =
      rawApiResponse.estimates.map(transformRawEstimate);

    return results;
  } catch (err) {
    const userFriendlyMessage = handleApiError(
      err,
      'submitting quote request',
      payload
    );
    throw new Error(userFriendlyMessage);
  }
};

/**
 * Transforms a raw estimate from the API into a standardized quote result.
 *
 * This function processes the raw estimate data from the API and determines whether
 * the contract can be canceled (returning a success quote) or must be denied
 * (returning a denial with reason). It handles business logic for non-cancellable
 * product types and rule violations.
 *
 * @param {RawQuoteEstimate} rawEstimate - The raw estimate data from the API
 * @returns {CancelQuoteResult} A processed quote result with either success details or denial reason
 *
 * @internal This function is used internally by submitQuoteRequest
 */
const transformRawEstimate = (
  rawEstimate: RawQuoteEstimate
): CancelQuoteResult => {
  const productTypeName = transformProductTypeName(rawEstimate);

  if (
    rawEstimate.cancellable &&
    (!rawEstimate.ruleViolations || rawEstimate.ruleViolations.length === 0)
  ) {
    return {
      status: 'quoted',
      contractNumber: rawEstimate.code,
      productTypeName: productTypeName,
      issuingDealer: rawEstimate.storeCode,
      effectiveDate: rawEstimate.effectiveDate,
      factor:
        rawEstimate.factor ??
        (rawEstimate.allFactors && rawEstimate.allFactors.length > 0
          ? rawEstimate.allFactors[0]
          : 'N/A'),
      allFactors: rawEstimate.allFactors ?? [],
      fee: parseFloat(rawEstimate.fee) || 0,
      totalClaims: parseFloat(rawEstimate.claimTotalAmount) || 0,
      claimsDeducted: rawEstimate.claimsDeducted,
      totalClaimAmountPaid: parseFloat(rawEstimate.claimTotalAmount) || 0,
      customerRefund: parseFloat(rawEstimate.customerRefund) || 0,
      customerPrice: parseFloat(rawEstimate.customerPrice) || 0,
      salesTax: parseFloat(rawEstimate.salesTax) || 0,
      planTerm: rawEstimate.planTerm || 'N/A',
    } as CancelQuoteSuccess;
  } else {
    let denialReason = rawEstimate?.ruleViolations?.[0]?.violationMessage;
    if (
      rawEstimate.productTypeCode == 'APP' ||
      rawEstimate.productTypeCode == 'VTA'
    ) {
      denialReason = 'This product is non-cancellable.';
    }
    return {
      status: 'denied',
      contractNumber: rawEstimate.code,
      productTypeName: productTypeName,
      issuingDealer: rawEstimate.storeCode,
      effectiveDate: rawEstimate.effectiveDate,
      denialReason: denialReason,
    } as CancelQuoteDenied;
  }
};

/**
 * Transforms raw product type codes into user-friendly product type names.
 *
 * This function converts internal product type codes from the API into
 * readable product names for display in the user interface.
 *
 * @param {RawQuoteEstimate} rawEstimate - The raw estimate containing product type information
 * @returns {string} A user-friendly product type name
 *
 * @internal This function is used internally by transformRawEstimate
 */
const transformProductTypeName = (rawEstimate: RawQuoteEstimate) => {
  switch (rawEstimate.productTypeCode) {
    case 'VSC':
      return 'Vehicle Service Contract';
    case 'MNT':
      return 'Prepaid Maintenance';
    default:
      return rawEstimate.productTypeName;
  }
};
